import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Collapse, Descriptions, Input, Modal, Progress, Select, Space, Tag, Tooltip, message } from 'antd';
import evmAdminService from '../../../shared/services/evmAdminService';
import { t } from '../../../shared/i18n';

const STATUS_COLOR = { queued: 'default', running: 'processing', success: 'success', failed: 'error' };
const STATUS_TEXT = { queued: '排队中', running: '部署中', success: '部署成功', failed: '部署失败' };
const errMsg = (res, fallback) => res?.msg || res?.error || fallback;

// 节点一键部署:环境检测 → 选组件 → 部署(后台任务,日志实时滚动;重跑即续跑)
const NodeDeployModal = ({ node, onClose, onDone }) => {
    const nodeId = node?.id;
    const [pre, setPre] = useState(null);
    const [preMsg, setPreMsg] = useState('');
    const [checking, setChecking] = useState(false);
    const [opts, setOpts] = useState({ kata: false, ceph: false, sync_images: true, bridge_mode: 'linux', physical_nic: '', upgrade_packages: false, force: false });
    const [job, setJob] = useState(null);
    const [log, setLog] = useState('');
    const [starting, setStarting] = useState(false);
    const offset = useRef(0);
    const timer = useRef(null);
    const logRef = useRef(null);
    const running = job && (job.status === 'queued' || job.status === 'running');

    const stop = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };

    const poll = useCallback(async (jobId) => {
        const res = jobId ? await evmAdminService.getDeployJob(jobId, offset.current)
            : await evmAdminService.getLatestDeployJob(nodeId, offset.current);
        if (res?.status === 'success' && res.data) {
            const d = res.data;
            if (d.log) setLog((l) => l + d.log);
            offset.current = d.log_size ?? offset.current;
            setJob(d);
            if (d.preflight) setPre(d.preflight);
            if (d.status === 'queued' || d.status === 'running') {
                timer.current = setTimeout(() => poll(d.id), 2000);
            } else if (d.status === 'success') {
                onDone?.();
            }
        }
    }, [nodeId, onDone]);

    useEffect(() => {
        stop();
        setJob(null); setLog(''); setPre(null); setPreMsg(''); offset.current = 0;
        if (nodeId) poll(null);
        return stop;
    }, [nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

    const check = async () => {
        setChecking(true);
        const res = await evmAdminService.checkNode(nodeId);
        setChecking(false);
        setPre(res?.data?.preflight || null);
        setPreMsg(res?.status === 'success' ? '' : errMsg(res, t('Check failed')));
    };

    const start = async () => {
        setStarting(true);
        const res = await evmAdminService.deployNode(nodeId, opts);
        setStarting(false);
        const jobId = res?.data?.job_id;
        if (res?.status !== 'success' && !jobId) { message.error(errMsg(res, t('Deployment failed'))); return; }
        if (res?.status !== 'success') message.warning(t('A deploy is already running on this node'));
        stop(); setLog(''); offset.current = 0;
        poll(jobId);
    };

    const set = (k) => (e) => setOpts((o) => ({ ...o, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
    const pct = job?.step_total ? Math.round((100 * (job.step_no || 0)) / job.step_total) : 0;
    const blocked = pre && (!pre.os_supported || !pre.virt_flag);

    return (
        <Modal open={!!node} title={`${t('One-click deploy')} · ${node?.name || ''}`} width={820} onCancel={() => { stop(); onClose(); }} footer={null} destroyOnClose>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space>
                    <Button onClick={check} loading={checking} disabled={running}>{t('Preflight check')}</Button>
                    {pre && (blocked ? <Tag color="error">{t('Cannot deploy')}</Tag> : <Tag color="success">{t('Ready to deploy')}</Tag>)}
                </Space>
                {preMsg && <Alert type="error" showIcon message={preMsg} />}
                {pre && (
                    <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label={t('System')}>{`${pre.os} ${pre.os_version} ${pre.arch}`}</Descriptions.Item>
                        <Descriptions.Item label={t('Virtualization')}>
                            {pre.virt_flag ? <Tag color="success">{pre.virt_flag}{pre.nested === 'Y' || pre.nested === '1' ? ' · nested' : ''}</Tag> : <Tag color="error">{t('Not supported')}</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label="CPU">{`${pre.cpus} × ${pre.cpu_model || ''}`}</Descriptions.Item>
                        <Descriptions.Item label={t('Memory')}>{`${Math.round((pre.mem_mb || 0) / 1024)} GB`}</Descriptions.Item>
                        <Descriptions.Item label={t('Disk')}>{(pre.disks || []).join(', ')}{` · /home ${pre.home_free_gb}G`}</Descriptions.Item>
                        <Descriptions.Item label={t('Public IP')}>{(pre.public_ips || []).join(', ')}</Descriptions.Item>
                        <Descriptions.Item label={t('NIC')} span={2}>{`${pre.uplink_nic} ${pre.uplink_ip}`}</Descriptions.Item>
                    </Descriptions>
                )}
                <Space wrap>
                    <Checkbox checked={opts.kata} onChange={set('kata')} disabled={running}>{t('Kata containers')}</Checkbox>
                    <Checkbox checked={opts.ceph} onChange={set('ceph')} disabled={running}>{t('Ceph storage')}</Checkbox>
                    <Checkbox checked={opts.sync_images} onChange={set('sync_images')} disabled={running}>{t('Sync images')}</Checkbox>
                </Space>
                <Collapse size="small" items={[{ key: 'adv', label: t('Advanced'), children: (
                    <Space direction="vertical">
                        <Space>
                            <Select value={opts.bridge_mode} onChange={set('bridge_mode')} style={{ width: 160 }} options={[{ value: 'linux', label: 'Linux Bridge' }, { value: 'ovs', label: 'Open vSwitch' }]} />
                            <Input value={opts.physical_nic} onChange={set('physical_nic')} placeholder={t('NIC (blank = auto)')} style={{ width: 200 }} />
                        </Space>
                        <Checkbox checked={opts.upgrade_packages} onChange={set('upgrade_packages')}>{t('Upgrade installed packages')}</Checkbox>
                        <Tooltip title={t('Restart the agent even while tasks or backups are running')}>
                            <Checkbox checked={opts.force} onChange={set('force')}>{t('Force agent restart')}</Checkbox>
                        </Tooltip>
                    </Space>
                ) }]} />
                <Button type="primary" onClick={start} loading={starting} disabled={running || blocked}>
                    {job ? t('Redeploy') : t('Deploy')}
                </Button>
                {job && (
                    <>
                        <Space>
                            <Tag color={STATUS_COLOR[job.status]}>{t(STATUS_TEXT[job.status] || job.status)}</Tag>
                            {job.step && <span>{`${job.step_no}/${job.step_total} ${job.step}`}</span>}
                            {job.agent_version && <Tag>{`agent ${job.agent_version}`}</Tag>}
                        </Space>
                        <Progress percent={job.status === 'success' ? 100 : pct} status={job.status === 'failed' ? 'exception' : (running ? 'active' : undefined)} size="small" />
                        {job.status === 'failed' && job.error && <Alert type="error" showIcon message={job.error} />}
                        <pre ref={logRef} style={{ maxHeight: 360, overflow: 'auto', background: '#111', color: '#ddd', padding: 12, fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {log.split('\n').filter((l) => !l.startsWith('::')).join('\n')}
                        </pre>
                    </>
                )}
            </Space>
        </Modal>
    );
};

export default NodeDeployModal;
